/**
 * FILE: packages\utils\src\attemptCanonical.ts
 * PURPOSE: Shared business utility.
 * AGENT_CONTEXT: Pure functions. No React, no Supabase client imports.
 * LAST_REVIEWED: 2026-05-07
 */

/**
 * Canonical course/attempt formatting — single source of truth for Hub + services.
 */

import { normaliseCourse } from './course.ts'

const MONTH_NUMBERS: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
}

const MONTH_INDEX: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
}

/** CFA/FRM exam months + Jan for edge legacy rows */
const VALID_ATTEMPT_MONTHS = new Set([
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
])

const STORAGE_ATTEMPT_PATTERN = /^[A-Z][a-z]{2}_[0-9]{2}$/
const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}/
const DURATION_PATTERN = /\bmonths?\b/i

const MONTH_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function capitalizeMonth(month: string): string {
  const lower = month.trim().toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

/** CFA L2 / CFA_L2 → CFA_L2 */
export function canonicalCourseForStorage(course: string): string {
  const trimmed = course.trim()
  if (!trimmed) return ''
  const normalized = normaliseCourse(trimmed)
  if (normalized && normalized !== trimmed) return normalized
  if (trimmed.includes(' ')) return trimmed.replace(/ /g, '_')
  return trimmed
}

/** Aug 2027 / Aug_27 / aug-27 → Aug_27 */
export function canonicalAttemptForStorage(attempt: string): string {
  const trimmed = attempt.trim()
  if (!trimmed) return ''

  if (trimmed.includes('_')) {
    const [month, year] = trimmed.split('_')
    if (!month || !year) return trimmed
    const yy = year.length === 4 ? year.slice(-2) : year
    return `${capitalizeMonth(month)}_${yy}`
  }

  const sep = trimmed.includes(' ') ? ' ' : trimmed.includes('-') ? '-' : null
  if (sep) {
    const [month, year] = trimmed.split(sep)
    if (!month || !year) return trimmed
    const yy = year.length === 4 ? year.slice(-2) : year
    return `${capitalizeMonth(month)}_${yy}`
  }

  return trimmed
}

/** Strict Mon_YY storage form only (display formats like Aug 2027 are invalid). */
export function isValidStorageAttempt(attempt: string | null | undefined): boolean {
  if (!attempt?.trim()) return false
  const trimmed = attempt.trim()
  if (!trimmed.includes('_')) return false
  const stored = canonicalAttemptForStorage(trimmed)
  if (!STORAGE_ATTEMPT_PATTERN.test(stored)) return false
  const month = stored.split('_')[0]
  if (!month) return false
  return VALID_ATTEMPT_MONTHS.has(capitalizeMonth(month))
}

/**
 * Best-effort Mon_YY for matching only (community link lookup).
 * - Already Mon_YY → validate + return
 * - Timestamp `YYYY-MM-DD...` → calendar month Mon_YY (no DB write)
 * - `Aug 2027`, `aug-27` → canonicalAttemptForStorage
 * - `1 Month`, `6 months` → null
 */
export function coerceAttemptForMatching(attempt: string | null | undefined): string | null {
  if (!attempt?.trim()) return null
  const trimmed = attempt.trim()

  if (DURATION_PATTERN.test(trimmed)) return null

  if (TIMESTAMP_PATTERN.test(trimmed)) {
    const datePart = trimmed.slice(0, 10)
    const [yearStr, monthStr] = datePart.split('-')
    const year = Number.parseInt(yearStr ?? '', 10)
    const month = Number.parseInt(monthStr ?? '', 10)
    if (!year || month < 1 || month > 12) return null
    const monthAbbr = MONTH_ABBRS[month - 1]
    const yy = String(year % 100).padStart(2, '0')
    const coerced = `${monthAbbr}_${yy}`
    return isValidStorageAttempt(coerced) ? coerced : null
  }

  const stored = canonicalAttemptForStorage(trimmed)
  return isValidStorageAttempt(stored) ? stored : null
}

/** UI dropdown filter: valid + isFutureOrCurrentAttempt */
export function filterAttemptsForUiDropdown(attempts: string[]): string[] {
  return attempts.filter((a) => isValidStorageAttempt(a) && isFutureOrCurrentAttempt(a))
}

/** Aug_27 → Aug 2027 */
export function displayAttemptFromStorage(attempt: string): string {
  const stored = canonicalAttemptForStorage(attempt)
  const [month, year] = stored.split('_')
  if (!month || !year) return attempt.trim()
  const fullYear = year.length === 2 ? `20${year}` : year
  return `${month} ${fullYear}`
}

/** Month abbreviation → MM for valid_till calculation */
export function attemptMonthToNumber(month: string): string | null {
  return MONTH_NUMBERS[capitalizeMonth(month)] ?? null
}

export function attemptMonthIndex(month: string): number | undefined {
  return MONTH_INDEX[capitalizeMonth(month)]
}

function courseVariants(course: string): string[] {
  const variants = [course]
  if (course.includes('_')) variants.push(course.replace(/_/g, ' '))
  if (course.includes(' ')) variants.push(course.replace(/ /g, '_'))
  return [...new Set(variants.map((v) => v.trim()))]
}

function attemptVariants(attempt: string): string[] {
  const variants = [attempt]
  const coerced = coerceAttemptForMatching(attempt)
  if (coerced) variants.push(coerced)
  const stored = canonicalAttemptForStorage(attempt)
  const display = displayAttemptFromStorage(attempt)
  variants.push(stored, display)
  if (attempt.includes('_')) {
    const [month, year] = attempt.split('_')
    variants.push(`${month} 20${year}`)
    variants.push(`${month} ${year}`)
  }
  if (attempt.includes(' ')) {
    const [month, year] = attempt.split(' ')
    if (year?.length === 4) variants.push(`${month}_${year.slice(2)}`)
    variants.push(`${month}_${year}`)
  }
  return [...new Set(variants.map((v) => v.trim()))]
}

export function coursesMatch(a: string | null, b: string | null): boolean {
  if (!a || !b) return false
  const aSet = new Set(courseVariants(a))
  return courseVariants(b).some((v) => aSet.has(v))
}

export function attemptsMatch(a: string | null, b: string | null): boolean {
  if (!a || !b) return false
  const aSet = new Set(attemptVariants(a))
  return attemptVariants(b).some((v) => aSet.has(v))
}

function parseAttemptParts(attempt: string | null): { month: string; year: number } | null {
  if (!attempt) return null
  const stored = canonicalAttemptForStorage(attempt)
  const [month, yearCode] = stored.split('_')
  if (!month || !yearCode) return null
  const year = yearCode.length === 2 ? 2000 + Number.parseInt(yearCode, 10) : Number.parseInt(yearCode, 10)
  if (Number.isNaN(year)) return null
  return { month: capitalizeMonth(month), year }
}

/** True when attempt month/year is current or in the future (any exam month). */
export function isFutureOrCurrentAttempt(attempt: string | null): boolean {
  const parts = parseAttemptParts(attempt)
  if (!parts) return false

  const monthIdx = attemptMonthIndex(parts.month)
  if (monthIdx === undefined) return false

  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonthIdx = now.getMonth()

  if (parts.year > currentYear) return true
  if (parts.year < currentYear) return false
  return monthIdx >= currentMonthIdx
}

export function attemptSortComparator(a: string, b: string): number {
  const pa = parseAttemptParts(a)
  const pb = parseAttemptParts(b)
  if (!pa && !pb) return a.localeCompare(b)
  if (!pa) return 1
  if (!pb) return -1
  const yearDiff = pa.year - pb.year
  if (yearDiff !== 0) return yearDiff
  return (attemptMonthIndex(pa.month) ?? 0) - (attemptMonthIndex(pb.month) ?? 0)
}

/** Merge registry attempts with ATTEMPTS_BY_COURSE fallback, deduped and sorted. */
export function mergeAttemptOptions(registry: string[], fallback: string[]): string[] {
  return [...new Set([...registry, ...fallback])].sort(attemptSortComparator)
}
